import { useEffect, useRef, useState } from "react";
import {
  IconArrowRight,
  IconArrowUpRight,
  IconChevronDown,
  IconCoin,
  IconInfinity,
  IconTargetArrow,
  IconX,
} from "@tabler/icons-react";
import headerImg from "../assets/header.png";

const REST_TILT_X = -8; // rotateY at rest
const REST_TILT_Y = 2; // rotateX at rest

export function Hero() {
  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      {/* Hero-only dot field (fades to transparent at the bottom edge so
          it seams into the next section). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-hero-dots"
      />

      {/* Asymmetric brand wash — both glows kept well clear of the bottom
          edge so the section seam is solid bg-bg on both sides. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 top-1/5 h-[520px] w-[640px] rounded-full bg-main-500/10 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-20 top-1/2 h-[420px] w-[560px] -translate-y-1/2 rounded-full bg-main-700/12 blur-[140px]"
      />

      <div className="relative mx-auto w-full max-w-[1440px] px-6 py-28 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[6fr_7fr] lg:gap-16">
          <CopyColumn />
          <ImageColumn />
        </div>
      </div>

      <ScrollDownButton />
    </section>
  );
}

function CopyColumn() {
  return (
    <div className="relative z-10 text-left">
      <h1 className="text-display-gradient text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
        Skip tracing,
        <br />
        without the fluff.
      </h1>

      <p className="mt-6 max-w-md text-base text-text-soft sm:text-lg">
        Turn any property address into the owner's verified phone and email.
        Skip tracing built for real estate investors — 98% hit rate, from
        1¢ a trace at scale.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-5">
        <a
          href="https://app.titanskip.com"
          className="group inline-flex h-11 items-center gap-2 rounded-full bg-main-500 pl-5 pr-4 text-sm font-semibold text-white transition hover:bg-main-400"
        >
          Get started
          <span
            aria-hidden
            className="grid h-6 w-6 place-items-center rounded-full bg-white/15 transition group-hover:translate-x-0.5 group-hover:bg-white/25"
          >
            <IconArrowRight size={12} stroke={2.6} />
          </span>
        </a>
        <a
          href="https://app.titanskip.com/api-docs"
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-text-soft transition hover:text-text"
        >
          Read the docs
          <IconArrowUpRight
            size={14}
            stroke={2.4}
            className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      </div>

      <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-text-muted">
        <li className="inline-flex items-center gap-1.5">
          <IconTargetArrow size={13} stroke={2} className="text-main-300" />
          98% hit rate
        </li>
        <li className="inline-flex items-center gap-1.5">
          <IconCoin size={13} stroke={2} className="text-main-300" />
          From 1¢ at scale
        </li>
        <li className="inline-flex items-center gap-1.5">
          <IconInfinity size={13} stroke={2} className="text-main-300" />
          No monthly minimums
        </li>
      </ul>
    </div>
  );
}

function ScrollDownButton() {
  const handleClick = () => {
    const target = document.getElementById("pricing");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll down"
      className="group absolute bottom-8 left-1/2 z-10 inline-flex -translate-x-1/2 flex-col items-center gap-1.5 text-text-muted transition hover:text-text"
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
        Scroll
      </span>
      <span className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/4 backdrop-blur transition group-hover:border-white/20 group-hover:bg-white/8">
        <IconChevronDown
          size={16}
          stroke={2.2}
          className="animate-scroll-hint text-main-300"
        />
      </span>
    </button>
  );
}

function ImageColumn() {
  const wrapperRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Cursor-follow tilt — write CSS variables directly via the ref so we
  // don't trigger React re-renders on every pointermove.
  const handlePointerMove = (e) => {
    if (e.pointerType !== "mouse" || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width; // 0..1
    const y = (e.clientY - rect.top) / rect.height; // 0..1
    const rotateY = REST_TILT_X + (0.5 - x) * 20; // -18..2
    const rotateX = REST_TILT_Y + (y - 0.5) * 16; // -6..10
    wrapperRef.current.style.setProperty("--tilt-x", `${rotateY.toFixed(2)}deg`);
    wrapperRef.current.style.setProperty("--tilt-y", `${rotateX.toFixed(2)}deg`);
  };

  const handlePointerLeave = () => {
    if (!wrapperRef.current) return;
    wrapperRef.current.style.setProperty("--tilt-x", `${REST_TILT_X}deg`);
    wrapperRef.current.style.setProperty("--tilt-y", `${REST_TILT_Y}deg`);
  };

  return (
    <>
      <div className="relative">
        {/* Soft single glow behind the slab */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-8 -z-10 rounded-4xl bg-main-500/10 blur-2xl"
        />

        {/* Tilt wrapper — perspective + click target + cursor tracking */}
        <div
          ref={wrapperRef}
          role="button"
          tabIndex={0}
          aria-label="View screenshot full size"
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onClick={() => setModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setModalOpen(true);
            }
          }}
          className="relative cursor-zoom-in rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-main-500 focus-visible:ring-offset-4 focus-visible:ring-offset-bg"
          style={{
            perspective: "2000px",
            "--tilt-x": `${REST_TILT_X}deg`,
            "--tilt-y": `${REST_TILT_Y}deg`,
          }}
        >
          <img
            src={headerImg}
            alt="TitanSkip control panel — Skip Trace Jobs"
            draggable={false}
            className="relative w-full select-none rounded-xl border border-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)] transition-transform duration-200 ease-out will-change-transform"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "100%",
              transform: "rotateY(var(--tilt-x)) rotateX(var(--tilt-y))",
              transformOrigin: "center center",
            }}
          />
        </div>
      </div>

      <ImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        src={headerImg}
        alt="TitanSkip control panel — Skip Trace Jobs"
      />
    </>
  );
}

function ImageModal({ open, onClose, src, alt }) {
  // Mount-on-open with a one-frame delay before flipping to "visible" so the
  // CSS transition can run from opacity-0 → opacity-100. Avoids the FOUC where
  // the modal HTML would briefly paint before Tailwind's CSS applied.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div
      className={`fixed inset-0 z-60 transition-opacity duration-300 ease-out ${
        visible
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0"
      }`}
    >
      {/* Backdrop — clicking it closes */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-zoom-out bg-black/70 backdrop-blur-2xl"
      />

      {/* Image — prevent backdrop click from firing when clicking the image */}
      <div className="pointer-events-none absolute inset-0 grid place-items-center p-6 sm:p-12">
        <img
          src={src}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
          className={`pointer-events-auto max-h-[90vh] max-w-[95vw] rounded-xl border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)] transition-transform duration-300 ease-out ${
            visible ? "scale-100" : "scale-95"
          }`}
        />
      </div>

      {/* Close button */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-6 top-6 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
      >
        <IconX size={18} stroke={2} />
      </button>
    </div>
  );
}
